/**
 * Welke relatietypen mogen tussen twee objecten, gegeven hun typen én hun
 * stereotypes.
 *
 * De matrix valideert op het basistype en weet niet welke extensies (c4 /
 * sharp / bmc) op de eindpunten actief zijn. Zonder de tweede stap verschijnt
 * `c4.Uses` tussen twee Sharp-«Concept»en en `sharp.ConceptAssociation` tussen
 * twee BMC-blokken, terwijl elke extensie-spec zegt dat dat niet hoort.
 *
 * 1. **Expliciete whitelist** — declareert een stereotype op bron of doel
 *    `allowed_relation_types`, dan gelden die plus alle relaties zonder
 *    extensie-voorvoegsel.
 * 2. **Voorvoegsel-terugval** — anders mag een relatie met voorvoegsel `c4.` /
 *    `sharp.` / `bmc.` alleen tussen eindpunten waarvan er minstens één een
 *    stereotype van diezelfde extensie draagt.
 *
 * Staat hier (en niet alleen in de web-client) zodat de server hetzelfde
 * antwoord geeft aan een client zonder canvas, zoals de MCP-server.
 */
import type { MatrixValidator } from "./matrix";
import type { RegistryRuntime } from "./registry";

const EXTENSION_PREFIXES = ["c4", "sharp", "bmc"] as const;

function extensionPrefix(typeId: string): string | null {
  const prefix = typeId.split(".")[0];
  return prefix !== undefined && (EXTENSION_PREFIXES as readonly string[]).includes(prefix)
    ? prefix
    : null;
}

export function filterRelationTypesByActiveExtensions(
  registry: RegistryRuntime,
  typeIds: readonly string[],
  sourceStereotypes: readonly string[] | undefined,
  targetStereotypes: readonly string[] | undefined,
): string[] {
  const explicit = registry.getAllowedRelationTypesForStereotypes([
    ...(sourceStereotypes ?? []),
    ...(targetStereotypes ?? []),
  ]);
  if (explicit !== null) {
    return typeIds.filter((t) => extensionPrefix(t) === null || explicit.has(t));
  }
  const active = new Set<string>();
  for (const s of [...(sourceStereotypes ?? []), ...(targetStereotypes ?? [])]) {
    const prefix = extensionPrefix(s);
    if (prefix) active.add(prefix);
  }
  return typeIds.filter((t) => {
    const prefix = extensionPrefix(t);
    return prefix === null || active.has(prefix);
  });
}

/**
 * Relatietypen die de matrix tussen deze twee elementtypen toelaat, beperkt
 * tot de actieve set. Matchen gebeurt op het basistype: een workspace-subtype
 * heeft geen eigen matrixregels.
 */
export function validRelationTypeIds(
  registry: RegistryRuntime,
  matrix: MatrixValidator,
  sourceTypeId: string,
  targetTypeId: string,
): string[] {
  const source = registry.getElementType(sourceTypeId);
  const target = registry.getElementType(targetTypeId);
  if (!source || !target) return [];
  const sourceRef = {
    id: registry.baseTypeIdFor(sourceTypeId),
    layer: source.layer,
    aspect: source.aspect,
  };
  const targetRef = {
    id: registry.baseTypeIdFor(targetTypeId),
    layer: target.layer,
    aspect: target.aspect,
  };
  return registry
    .listActiveRelationTypes()
    .filter((rt) => matrix.validate(rt.id, sourceRef, targetRef).valid)
    .map((rt) => rt.id);
}
